const NUDGE_TRAY_STATE_KEY = 'cl-nudge-tray'
import { NavState } from '../nav/nav'
import Messenger from '../messenger/messenger'
import { PubsubEvent } from '../pubsub/pubsub'
import { DonationChangedMsgFromApp } from '../messenger/messenger.messages'
import DAO from '../dao/dao'
import { ConfigResponse } from '../config/config.types'
import { Configs } from '../config/config.service'

interface AbandonCartNudgeTrayModelStateConfig {
  enabled: boolean
  theme: ConfigResponse['theme']
  organizationName?: string
}

export interface AbandonCartNudgeTrayModelState {
  nav: NavState
  config: AbandonCartNudgeTrayModelStateConfig
}

interface PersistedAbandonCartNudgeTrayState extends NavState {
  hasInteractedWithDonationForm: true
}

interface AbandonCartNudgeTrayModelProps {
  win: Window
  msg: Messenger
  configs: Configs
}

export default class AbandonCartNudgeTrayModel {
  props: AbandonCartNudgeTrayModelProps
  private dao: DAO
  private destroyed = false
  private hasTrackedModalOpen = false

  constructor(props: AbandonCartNudgeTrayModelProps) {
    this.props = props
    this.dao = new DAO({ win: this.props.win })
    this.props.msg.onDonationChanged(this.onDonationChanged)
    this.props.msg.onDonationCompleted(this.onDonationCompleted)
  }

  public destroy(): void {
    if (this.destroyed) {
      return
    }
    this.destroyed = true
  }

  public set = (state: NavState): void => {
    const persistedState: PersistedAbandonCartNudgeTrayState = {
      ...state,
      hasInteractedWithDonationForm: true,
    }

    this.props.win.localStorage.setItem(NUDGE_TRAY_STATE_KEY, JSON.stringify(persistedState))
  }

  public reset = (): void => {
    this.props.win.localStorage.removeItem(NUDGE_TRAY_STATE_KEY)
  }

  public markModalOpened = (): void => {
    this.hasTrackedModalOpen = true
  }

  public get = (): AbandonCartNudgeTrayModelState | null => {
    try {
      const rawState = this.props.win.localStorage.getItem(NUDGE_TRAY_STATE_KEY)

      if (!rawState) {
        return null
      }

      const parsedNavState = JSON.parse(rawState)

      if (!this.isValidState(parsedNavState)) {
        return null
      }

      const campaignId = (parsedNavState as PersistedAbandonCartNudgeTrayState).campaign

      if (!campaignId) {
        return null
      }

      const nav = parsedNavState as NavState
      const config = this.getConfig(campaignId)

      if (!config?.enabled) {
        return null
      }

      return { nav, config }
    } catch (error) {
      return null
    }
  }

  private getConfig = (campaignId: string): AbandonCartNudgeTrayModelStateConfig | null => {
    const config = this.props.configs[campaignId]
    // If there is no config for this campaign, there is nothing to render
    // and the nudge tray should be disabled gracefully.
    if (!config) {
      return null
    }
    const nudgeTrayComponent = Object.values(config.components).find(
      (component) => component.type === 'nudge-tray'
    )

    if (!nudgeTrayComponent) {
      return null
    }

    return {
      enabled: nudgeTrayComponent.props.abandonCartNudgeEnabled,
      theme: config.theme,
      organizationName: config.organizationName,
    }
  }

  private onDonationChanged = (event: PubsubEvent<DonationChangedMsgFromApp>) => {
    if (!this.hasTrackedModalOpen) {
      return
    }

    if (event.data.payload.campaignId && event.data.payload.params) {
      this.set({
        campaign: event.data.payload.campaignId,
        params: event.data.payload.params,
      })
    }
  }

  private onDonationCompleted = () => {
    this.reset()
  }

  private isValidState = (state: Record<string, unknown>) => {
    return (
      this.hasInteractedWithDonationForm(state.hasInteractedWithDonationForm) &&
      this.isValidStateCampaign(state.campaign) &&
      this.isValidStateParams(state.params)
    )
  }

  private hasInteractedWithDonationForm = (value?: unknown) => {
    return value === true
  }

  private isValidStateCampaign = (value?: unknown) => {
    return !!value && typeof value === 'string'
  }

  private isValidStateParams = (value?: unknown) => {
    return typeof value === 'object' && !Array.isArray(value) && value !== null
  }
}
